import Vue from "vue";
import { createAuth0Client } from "@auth0/auth0-spa-js";

import decode from "jwt-decode";
import lodashGet from "lodash/get";

import {
  AUTH0_TENANT_CONFIGS,
  AUTH0_AUDIENCE,
  AUTH0_CALLBACK,
} from "@/common/config";
import {
  getIdToken,
  getAccessToken,
  clearAllStorage,
  setAccessToken,
  setIdToken,
} from "@/common/storage";
import { setUserGroups, getUserGroups } from "./storage";
import { hasGroupByName } from "@/services/user/user.service";
import ROUTER from "@/router";
import { getLockedFeatures } from "@/common/featureLock";
import store from "@/store";

import { deleteToken as unregisterDeviceForNotifications } from "@/services/common/messaging.service";
import { getTopicsConfig } from "@/services/events/topics.service";
import { getAttackTypesConfig } from "@/services/events/events.service";
import { getPerpetratorsConfig } from "@/services/events/events.service";
import { getMaritimeSubcategoriesConfig } from "@/services/events/events.service";
import { getMaritimeVesselTypesConfig } from "@/services/events/events.service";

import { getRiskCategoriesConfig } from "@/services/controlRisks/risks.service";

export { _login as login };
export { _logout as logout };
export { _requireAuth as requireAuth };
export { _isLoggedIn as isLoggedIn };
export { _checkSession as checkSession };
export { _getRequestHeaders as getRequestHeaders };
export { _authReady as authReady };
export { _findCookie as findCookie };
export { _getDesiredAuthRegion as getDesiredAuthRegion };
export { _setDesiredAuthRegion as setDesiredAuthRegion };
export { _getAuthTenantConfig as getAuthTenantConfig };

const AUTH_REGION_COOKIE_NAME = "AuthRegion";

let instance;

/** Returns the current instance of the SDK */
export const getInstance = () => instance;

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = ({
  onRedirectCallback = () => {
    console.error("default redirect used");
  },
  redirectUri = AUTH0_CALLBACK,
  ...options
}) => {
  if (instance) return instance;

  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        auth0user: {},
        auth0Client: null,
        popupOpen: false,
        error: null,
      };
    },
    methods: {
      /** Handles the callback when logging in using a redirect */
      async handleRedirectCallback() {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          this.auth0user = await this.auth0Client.getUser();
          this.isAuthenticated = true;
          this.authenticationError = null;
        } catch (e) {
          this.authenticationError = e;
        } finally {
          this.loading = false;
        }
      },
      /** Authenticates the user using the redirect method */
      loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o);
      },
      /** Returns all the claims present in the ID token */
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o);
      },
      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      async getTokenSilently(o) {
        try {
          let res = await this.auth0Client.getTokenSilently(o);
          return res;
        } catch (err) {
          if (err && err.error === "login_required") {
            return null;
          } else {
            throw err;
          }
        }
      },
      /** Gets the access token using a popup window */

      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      /** Logs the user out and removes their session on the authorization server */
      logout(o) {
        return this.auth0Client.logout(o);
      },

      getAuth0Client() {
        return this.auth0Client;
      },
    },
    /** Use this lifecycle method to instantiate the SDK client */
    async created() {
      // Create a new instance of the SDK client using members of the given options object
      this.auth0Client = await createAuth0Client({
        ...options,
        authorizationParams: {
          audience: AUTH0_AUDIENCE,
          redirect_uri: redirectUri,
        },
      });

      try {
        // If the user is returning to the app after authentication..
        if (
          window.location.search.includes("code=") &&
          window.location.search.includes("state=")
        ) {
          // handle the redirect and retrieve tokens
          await this.auth0Client.handleRedirectCallback();

          this.authenticationError = null;
        }
      } catch (e) {
        console.log(e);
        this.authenticationError = e;
      } finally {
        this.loading = false;
      }
    },
  });

  return instance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options);
  },
};

const _login = async (opts = {}) => {
  await _authReady();
  if (await _isLoggedIn()) {
    await _logout();
  }
  instance.$auth.loginWithRedirect(opts);
};

const _logout = async (opts = {}) => {
  if (opts.unauthenticated) {
    clearAllStorage();
    if (instance && instance.$auth) {
      await instance.$auth.logout({
        logoutParams: {
          returnTo: window.origin,
        },
      });
    } else {
      window.location = window.location;
    }
  } else {
    await _authReady();
    await unregisterDeviceForNotifications();
    clearAllStorage();
    await instance.$auth.logout({
      logoutParams: {
        returnTo: window.origin,
      },
    });
  }
};

const _setLockedFeatures = async () => {
  if (store) {
    const LOCKED_FEATURES = store.getters["home/lockedFeatures"];
    if (!LOCKED_FEATURES) {
      await getLockedFeatures();
    }
  }
};

const _requireAuth = async (to, from, next) => {
  if (!(await _isLoggedIn())) {
    next({
      path: "/",
      query: { redirect: to.fullPath },
    });
  } else {
    await getTopicsConfig();
    await getAttackTypesConfig();
    await getPerpetratorsConfig();
    await getMaritimeSubcategoriesConfig();
    await getMaritimeVesselTypesConfig();
    await getRiskCategoriesConfig();
    await setUserGroups(); /* TODO: Decide if we want this call here or only on login.
                                    With it here if we change user groups we don't need
                                    a logout/login (just a refresh), but the call is made more frequently. */

    const GROUPS = getUserGroups();
    if (GROUPS) {
      if (Array.isArray(GROUPS) && GROUPS.length) {
        if (hasGroupByName("Mobile_Only")) {
          return next("/mobile-only");
        }
        await _setLockedFeatures();
        next();
      } else {
        console.log("User currently assigned to no groups, so new user.");
        return next();
      }
    } else {
      console.log(
        "Unable to sign in due to session or groups.",
        "GROUPS=",
        GROUPS
      );
      next({
        path: "/",
        query: { redirect: to.fullPath },
      });
    }
  }
};

const hasTokens = function () {
  const ID_TOKEN = getIdToken();
  const ACCESS_TOKEN = getAccessToken();
  return ID_TOKEN && ACCESS_TOKEN;
};

const attemptSilentAuthentication = async function () {
  if (!hasTokens()) {
    return false;
  }
  const threshold = 10;
  const ID_TOKEN = getIdToken();
  const ACCESS_TOKEN = getAccessToken();
  const now = new Date();
  const minsTilExpireID = (_getTokenExpirationDate(ID_TOKEN) - now) / 1000 / 60;
  const minsTilExpireACCESS =
    (_getTokenExpirationDate(ACCESS_TOKEN) - now) / 1000 / 60;
  if (minsTilExpireID < threshold || minsTilExpireACCESS < threshold) {
    await _checkSession();
  }
};

const _isLoggedIn = async () => {
  const ID_TOKEN = getIdToken();

  if (!!ID_TOKEN && !_isTokenExpired(ID_TOKEN)) {
    return true;
  } else {
    if (ROUTER && ROUTER.currentRoute.path !== "/") {
      await _logout({ unauthenticated: true });
    }
    clearAllStorage();
    return false;
  }
};

const _checkSession = async () => {
  await _authReady();
  try {
    let accessToken = await instance.$auth.getTokenSilently();
    if (accessToken) {
      setAccessToken(accessToken);
      let idTokenClaims = await instance.$auth.getIdTokenClaims();
      setIdToken(idTokenClaims.__raw);
      return true;
    }
  } catch (err) {
    console.error("failed getTokenSilently", err);
  }
  await _logout({ unauthenticated: true });
  return false;
};

const _getTokenExpirationDate = (encodedToken) => {
  const TOKEN = decode(encodedToken);
  if (!TOKEN.exp) {
    return null;
  }

  const DATE = new Date(0);
  DATE.setUTCSeconds(TOKEN.exp);

  return DATE;
};

const _isTokenExpired = (token) => {
  const EXPIRATION_DATE = _getTokenExpirationDate(token);
  return EXPIRATION_DATE < new Date();
};

const _getRequestHeaders = () => {
  return {
    Authorization: "Bearer " + getAccessToken(),
    "Content-type": "application/json",
  };
};

function isAuthReady() {
  let loading = lodashGet(instance, "$auth.loading");
  return typeof loading === "boolean" && !loading;
}

const _authReady = () => {
  return new Promise((resolve) => {
    if (isAuthReady()) {
      return resolve();
    }
    let intId = setInterval(async () => {
      if (isAuthReady()) {
        clearInterval(intId);
        return resolve();
      }
    }, 100);
  });
};

let _findCookie = (pName) => {
  let parts = document.cookie.split("; ");
  for (let i = 0; i < parts.length; i++) {
    if (parts[i].indexOf(pName) === 0) {
      let res = parts[i].split("=")[1];
      res.trim();
      return res;
    }
  }
};

let _getDesiredAuthRegion = function () {
  return _findCookie(AUTH_REGION_COOKIE_NAME);
};

let _setDesiredAuthRegion = function (val) {
  document.cookie = `${AUTH_REGION_COOKIE_NAME}=${val}`;
};

let _getAuthTenantConfig = function () {
  if (AUTH0_TENANT_CONFIGS.length === 1) {
    return AUTH0_TENANT_CONFIGS[0];
  }
  let desiredAuthRegion = _getDesiredAuthRegion();
  if (desiredAuthRegion) {
    for (let i = 0; i < AUTH0_TENANT_CONFIGS.length; i++) {
      if (AUTH0_TENANT_CONFIGS[i].name === desiredAuthRegion) {
        return AUTH0_TENANT_CONFIGS[i];
      }
    }
  }
};
